import { useEffect, useState } from 'react';
import { addStateInformation, StateInformation } from '../api/LocationSearch';
import { Property } from '../api/Property';

export function useMaps() {
    const [mapRef, setMapRef] = useState<google.maps.Map<Element> | null>(null);
    const [mapsRef, setMapsRef] = useState(null);
    const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
    const handleApiLoaded = (map: google.maps.Map, maps: any) => {
        setMapRef(map);
        setMapsRef(maps);
        setGeocoder(new maps.Geocoder());
    };

    return { mapRef, mapsRef, geocoder, handleApiLoaded };
}

export function useGeocodedState(geocoder: google.maps.Geocoder | null, state: StateInformation) {
    const [geocodedState, setGeocodedState] = useState<google.maps.LatLngBounds | undefined>(
        undefined
    );

    const swLatLng =
        state && state.swLat && state.swLng && geocoder
            ? new google.maps.LatLng(state.swLat, state.swLng)
            : undefined;

    const neLatLng =
        state && state.neLat && state.neLng && geocoder
            ? new google.maps.LatLng(state.neLat, state.neLng)
            : undefined;

    const updateStateInformation = (bounds: google.maps.LatLngBounds) => {
        const info: StateInformation = {
            stateInitials: state.stateInitials,
            stateName: state.stateName,
            swLat: bounds.getSouthWest().lat(),
            swLng: bounds.getSouthWest().lng(),
            neLat: bounds.getNorthEast().lat(),
            neLng: bounds.getNorthEast().lng()
        };

        // not awaited:
        addStateInformation(info);
    };

    const createGeocodedState = (sw: google.maps.LatLng, ne: google.maps.LatLng) => {
        setGeocodedState(new google.maps.LatLngBounds(sw, ne));
    };
    const geocodeState = (gc: google.maps.Geocoder) => {
        gc.geocode({ address: `State of ${state.stateInitials}` }, (results, status) => {
            if (
                status === google.maps.GeocoderStatus.OK &&
                results &&
                results.length > 0 &&
                results[0].geometry &&
                results[0].geometry.viewport
            ) {
                setGeocodedState(results[0].geometry.viewport);
                updateStateInformation(results[0].geometry.viewport);
            }
        });
    };

    useEffect(() => {
        if (geocoder && state) {
            if (swLatLng && neLatLng) {
                createGeocodedState(swLatLng, neLatLng);
            } else {
                geocodeState(geocoder);
            }
        }
    }, [geocoder, state]);

    return geocodedState;
}

export function useGeocodedProperties(
    geocoder: google.maps.Geocoder | null,
    properties: Property[]
) {
    const [geocodedProperties, setGeocodedProperties] = useState<Property[] | undefined>(undefined);

    const geocodeProperties = (gc: google.maps.Geocoder) => {
        var codedProperties: Property[] = [];
        if (properties.length > 0) {
            properties.forEach(property => {
                if (property.latitude && property.longitude) {
                    codedProperties.push(property);
                    if (codedProperties.length === properties.length) {
                        setGeocodedProperties(codedProperties);
                    }
                } else {
                    var codedProperty = { ...property };
                    const addressComponents = [
                        property.streetAddress,
                        property.city,
                        property.state,
                        property.zip
                    ];
                    const address = addressComponents.join(', ');
                    gc.geocode({ address: address }, (results, status) => {
                        if (status === google.maps.GeocoderStatus.OK) {
                            var position = results[0].geometry.location;
                            codedProperty.latitude = position.lat();
                            codedProperty.longitude = position.lng();
                        }
                        codedProperties.push(codedProperty);

                        if (codedProperties.length === properties.length) {
                            setGeocodedProperties(codedProperties);
                        }
                    });
                }
            });
        } else {
            // trigger reset to center of US
            setGeocodedProperties([]);
        }
    };

    useEffect(() => {
        if (geocoder) {
            geocodeProperties(geocoder);
        }
    }, [geocoder, properties]);

    return geocodedProperties;
}



// WEBPACK FOOTER //
// ./src/hooks/useMaps.tsx